import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { getShark } from "@/api/client";
import type { Shark, Sighting } from "@/api/types";
import { useAsync } from "@/hooks/useAsync";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatDate, formatLicense, pluralize, sourceLabel, viewLabel, yearOf } from "@/lib/format";
import { rarityInfo } from "@/lib/rarity";
import { grothTriangle } from "@/site/geometry";
import { Failure, Loading, RarityMark, SectionHead, to } from "@/site/parts";
import { Plate, sharkAspect } from "@/site/plate";
import { PhotoReel } from "@/site/reel";

function YearHistogram({ sightings }: { sightings: Sighting[] }) {
  const years = new Map<number, number>();
  for (const s of sightings) {
    const y = yearOf(s.date);
    if (y) years.set(Number(y), (years.get(Number(y)) ?? 0) + 1);
  }
  if (years.size === 0) return <p className="label">No dated sightings.</p>;
  const lo = Math.min(...years.keys());
  const hi = Math.max(...years.keys());
  const span = hi - lo + 1;
  const max = Math.max(...years.values());
  const every = Math.ceil(span / 8);
  return (
    <figure className="hist" style={{ margin: 0 }}>
      <figcaption className="sr-only">
        Sightings per year, {lo} to {hi}
      </figcaption>
      <ol className="hist-bars">
        {Array.from({ length: span }, (_, i) => {
          const year = lo + i;
          const n = years.get(year) ?? 0;
          const shown = i % every === 0 || year === hi;
          return (
            <li key={year}>
              <span className="sr-only">
                {year}: {n}
              </span>
              <span className="hist-n" aria-hidden="true">
                {n > 0 ? n : ""}
              </span>
              <span
                className="hist-bar"
                data-hot={year === hi}
                style={{ height: `calc((100% - 22px) * ${n / max})` }}
              />
              <span className="hist-year" aria-hidden="true">
                {shown ? year : ""}
              </span>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}

function Record({ shark }: { shark: Shark }) {
  const info = rarityInfo(shark.rarity);
  const rows: [string, React.ReactNode][] = [
    [
      "Catalogue no.",
      <span key="id" className="mono">
        {shark.id}
      </span>,
    ],
    ["Rarity", <RarityMark key="rarity" rarity={shark.rarity} />],
    ["Class", info.blurb],
    [
      "Encounters",
      <span key="enc" className="mono">
        {shark.encounters}
      </span>,
    ],
    ["First seen", formatDate(shark.first_seen) ?? "—"],
    ["Last seen", formatDate(shark.last_seen) ?? "—"],
    ["Regions", shark.regions.join(" · ") || "—"],
    [
      "Views on file",
      // Spots only match within a view, so a missing view is one this shark can't be
      // recognised from yet.
      shark.views.map((v) => viewLabel(v)).join(" · ") || "—",
    ],
    ["Named by", shark.named_by ?? (shark.name ? "—" : "Unnamed · epithet generated")],
    [
      "Catalogue aliases",
      shark.sources.map((s) => (
        <span key={`${s.source}-${s.source_id}`} style={{ display: "block" }}>
          {s.url ? (
            <a href={s.url} className="link-u" target="_blank" rel="noreferrer">
              {sourceLabel(s.source)}
            </a>
          ) : (
            sourceLabel(s.source)
          )}{" "}
          <span className="label">{s.source_id.slice(0, 18)}</span>
        </span>
      )),
    ],
  ];
  return (
    <dl className="kv">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function SightingTable({ sightings }: { sightings: Sighting[] }) {
  return (
    <div className="scroll-x">
      <table className="table">
        <thead>
          <tr>
            <th>Image</th>
            <th>Date</th>
            <th>Locality</th>
            <th>View</th>
            <th>Source</th>
            <th>Photographer</th>
            <th>Licence</th>
          </tr>
        </thead>
        <tbody>
          {sightings.map((s) => (
            <tr key={s.image_key}>
              <td>
                {s.thumb_url ? (
                  <img
                    src={s.thumb_url}
                    alt={`Sighting ${formatDate(s.date) ?? s.image_key}`}
                    className="sight-thumb"
                    loading="lazy"
                  />
                ) : (
                  <span className="label">Spots only</span>
                )}
              </td>
              <td className="mono" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                {s.date?.slice(0, 10) ?? "—"}
              </td>
              <td>{[s.locality, s.country].filter(Boolean).join(", ") || "—"}</td>
              <td className="label">{viewLabel(s.view) ?? "—"}</td>
              <td>
                {s.source_url ? (
                  <a href={s.source_url} className="link-u" target="_blank" rel="noreferrer">
                    {sourceLabel(s.source)}
                  </a>
                ) : (
                  sourceLabel(s.source)
                )}
              </td>
              <td>{s.photographer ?? "—"}</td>
              <td className="label">{formatLicense(s.license) ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SharkBody({ shark }: { shark: Shark }) {
  const c = shark.constellation;
  const tri = useMemo(() => (c ? grothTriangle(c) : null), [c]);
  const spotOnly = !shark.thumb_url;
  return (
    <>
      <section className="in-wrap in-grid" style={{ paddingTop: 40, rowGap: 32 }}>
        <div style={{ gridColumn: "span 12" }}>
          <p className="label">
            <Link to={to.registry} className="link-u">
              Registry
            </Link>{" "}
            / {shark.id}
          </p>
          <h1 className="display-2 anim-in" style={{ marginTop: 14, maxWidth: "14em" }}>
            {shark.display_name}
          </h1>
        </div>
        <figure style={{ gridColumn: "span 7", margin: 0, minWidth: 0 }} className="anim-in">
          {spotOnly ? (
            <>
              <div className="fig-head">
                <span className="label">Fig. A · Hand-marked spots</span>
                <span className="label">{c ? `${c.spots.length} spots` : ""}</span>
              </div>
              {c ? (
                <Plate
                  imageUrl={null}
                  constellation={c}
                  aspect={4 / 3}
                  edges
                  triangle={tri?.vertices ?? null}
                  label={`Hand-marked spot chart of ${shark.display_name}; the photograph is held by Sharkbook`}
                />
              ) : (
                <p className="note">No photograph or spot chart on file.</p>
              )}
              <figcaption className="fig-cap label">
                <span>Photograph held by Sharkbook · spots drawn from the catalogue export</span>
              </figcaption>
            </>
          ) : (
            <PhotoReel sightings={shark.sightings} name={shark.display_name} />
          )}
        </figure>
        <div style={{ gridColumn: "9 / span 4" }}>
          <Record shark={shark} />
        </div>
      </section>
      {c && !spotOnly && (
        <section className="in-wrap in-section" aria-labelledby="constellation">
          <SectionHead index="01" title="Constellation" id="constellation">
            The reference fingerprint: {c.spots.length} spots from the best catalogue image. A
            marked triangle shows the invariants the matcher compares.
          </SectionHead>
          <div className="in-grid" style={{ rowGap: 24 }}>
            <div style={{ gridColumn: "span 8" }}>
              <Plate
                imageUrl={null}
                constellation={c}
                aspect={sharkAspect(c, 1.2, 2.2)}
                edges
                triangle={tri?.vertices ?? null}
                label={`Spot constellation of ${shark.display_name}`}
              />
            </div>
            <dl className="kv" style={{ gridColumn: "9 / span 4", alignSelf: "start" }}>
              {[
                ["Spots", String(c.spots.length)],
                ["Frame", `${c.width} × ${c.height} px`],
                ["△ABC ratio R", tri ? tri.ratio.toFixed(3) : "—"],
                ["△ABC cosine C", tri ? tri.cosine.toFixed(3) : "—"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd className="mono">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}
      <section className="in-wrap in-section" aria-labelledby="sightings">
        <SectionHead index={c && !spotOnly ? "02" : "01"} title="Sightings" id="sightings">
          {pluralize(shark.sightings.length, "image")} on record, by year and in full.
        </SectionHead>
        <YearHistogram sightings={shark.sightings} />
        <div style={{ marginTop: 32 }}>
          <SightingTable sightings={shark.sightings} />
        </div>
      </section>
    </>
  );
}

export function SharkRecord() {
  const { id = "" } = useParams();
  const state = useAsync(`shark:${id}`, (signal) => getShark(id, signal));
  useDocumentMeta({
    title: state.data ? `${state.data.display_name} (${state.data.id})` : "Specimen record",
  });
  if (state.status === "loading") return <Loading label={`Retrieving ${id}`} />;
  if (state.status === "error") {
    return <Failure error={state.error} title={`No record for ${id}.`} onRetry={state.reload} />;
  }
  return <SharkBody shark={state.data} />;
}
